import React, { useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';

interface DealLeadInfoProps {
  dealInfo: any;
}

const DealLeadInfo: React.FC<DealLeadInfoProps> = ({ dealInfo }) => {
  const { theme } = useTheme();

  const leadAttributes = useMemo(() => {
    if (!dealInfo) return [];

    const attrs: { label: string; value: string }[] = [];

    try {
      const flexAttrs = dealInfo.flexAttributes
        ? typeof dealInfo.flexAttributes === 'string'
          ? JSON.parse(dealInfo.flexAttributes)
          : dealInfo.flexAttributes
        : {};

      // Add all flex attributes
      Object.entries(flexAttrs).forEach(([key, value]) => {
        if (
          value &&
          typeof value === 'string' &&
          value.trim() &&
          key !== 'photo' &&
          key !== 'pictures'
        ) {
          attrs.push({ label: key, value: value as string });
        }
      });

      // Add standard deal fields if not already present
      if (dealInfo.name && !attrs.find((a) => a.label === 'Name')) {
        attrs.unshift({ label: 'Name', value: dealInfo.name });
      }

      if (
        dealInfo.description &&
        !attrs.find((a) => a.label === 'Description')
      ) {
        attrs.push({ label: 'Description', value: dealInfo.description });
      }

      if (dealInfo.stage?.title) {
        attrs.push({ label: 'Stage', value: dealInfo.stage.title });
      }

      if (dealInfo.value) {
        attrs.push({
          label: 'Value',
          value: `$${Number(dealInfo.value).toLocaleString()}`,
        });
      }

      return attrs;
    } catch (e) {
      console.error('Error parsing flexAttributes:', e);
      return [];
    }
  }, [dealInfo]);

  const renderAttribute = (
    attr: { label: string; value: string },
    index: number
  ) => (
    <Grid item xs={12} sm={6} key={index}>
      <Box
        sx={{
          backgroundColor: theme.background2,
          borderRadius: 2,
          p: 2,
          height: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: theme.textLight,
            mb: 0.5,
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          {attr.label}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: theme.text,
            wordBreak: 'break-word',
          }}
        >
          {attr.value}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
          Lead Information
        </Typography>
      </Box>

      {leadAttributes.length > 0 ? (
        <Grid container spacing={1.5}>
          {leadAttributes.map((attr, index) => renderAttribute(attr, index))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography sx={{ color: theme.textLight, textAlign: 'center' }}>
            No lead information available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DealLeadInfo;
